// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Analyse — cycle de vie d'un rapport, de l'upload au résultat : assistant NORMA,
// étapes du pipeline, statuts, niveaux de conformité et résumé copiable.
//
// 💣 19 CLÉS PAR LANGUE NE SONT UTILISÉES PAR AUCUN COMPOSANT DE `main` :
// `mission_type_*` (10), `type_conf_*` (8) et `status_awaiting_type`. Elles
// décrivent la branche `feat/dta`, où `pages/Dashboard.js` les consomme ; elles
// sont arrivées seules, SANS leurs composants, par le backport `b09169a`.
//
// ⛔ Sur `main`, il n'existe NI sélecteur de mission à l'upload, NI boîte de
// confirmation de type : `utils/mission_filter.py` refuse à plat, avant toute
// dépense LLM, sans proposer d'alternative. Le seul sélecteur de mission vivant
// est celui des projets — `pages/Projects.js` : MISSION_TYPES = ['RAAT','DAAT'].
//
// ⚠️ La version précédente de ce commentaire affirmait AU PRÉSENT que DTA, fiche
// récapitulative, évaluation périodique et DAPP « sont proposés à la saisie mais
// REFUSÉS à l'analyse ». C'est vrai sur `feat/dta`, FAUX sur `main` — et cette
// phrase a fait rédiger un faux constat de défaut produit le 27/08/2026. Le
// contrôle qui tranche, avant toute affirmation sur ce qu'un écran propose :
//     grep -rl "<clé>" frontend/src --include=*.js | grep -v locales
// Vide = personne ne l'utilise, quel que soit le commentaire qui l'entoure.
//
// ⚠️ `{declared}` et `{detected}` dans `type_conf_*` sont des substitutions
// faites par le composant de `feat/dta` — ne pas les traduire ni les réordonner.
//
// `score_conformity`, lue par aucun composant (ni sur `main` ni sur
// `feat/dta`), a été retirée le 30/09/2026 sur décision de Marc : un avis,
// jamais un « taux de conformité ».
// ⛔ Les 19 clés ci-dessus restent : `feat/dta` les utilise. Les retirer
// casserait la branche à la fusion.

const analyse = {
  fr: {
    // ── Choix de la norme au dépôt (T2) — affiché seulement s'il y en a plusieurs ──
    depot_norme_label:                 'Type de diagnostic',
    depot_aucune_norme:                'Aucun type de diagnostic n\'est activé pour votre compte. Contactez Evalens pour en ouvrir un.',
    norme_non_activee:                 'Ce type de diagnostic n\'est pas activé pour votre compte. Contactez Evalens pour l\'ouvrir.',
    norme_inconnue:                    'Ce type de diagnostic n\'existe pas ou n\'est plus proposé.',
    // ── Upload ──
    upload_placeholder:                'Déposez ou cliquez sur + pour uploader votre rapport amiante',
    upload_dragging:                   'Déposez votre rapport PDF ici...',
    upload_validating:                 'Validation en cours...',
    val_pages:                         'pages',
    val_chars:                         'caractères',
    val_limit_pct:                     '% de la limite',
    plus_options_tooltip:              'Plus d\'options',
    plus_associate_project:            'Associer un projet',
    plus_no_projects:                  'Aucun projet créé',
    plus_draft:                        'Brouillon',

    // ── Quota épuisé ──
    // Le serveur refuse l'upload, le dépôt de document projet et le démarrage
    // d'analyse. Ces libellés préviennent AVANT l'essai : jusqu'au 27/07/2026,
    // l'utilisateur découvrait le refus par une popup « ❌ Erreur:
    // quota_exceeded » — un code technique, en anglais.
    quota_exhausted_badge:             'Quota épuisé',
    quota_exhausted_title:             'Vous avez utilisé toutes vos analyses.',
    quota_exhausted_body:              'Choisissez votre abonnement pour continuer.',
    quota_exhausted_cta:               'Voir les abonnements',
    // Forfait mensuel consommé chez un ABONNÉ : rien n'est bloqué, les
    // analyses suivantes sont facturées à l'unité. Prévenir avant le dépôt.
    forfait_epuise_title:              'Forfait mensuel épuisé',
    // ⛔ Aucun montant ici (décision du 15/09/2026) : le prix unitaire se lit
    // dans l'espace client et la page Abonnement, pas sur l'écran de dépôt.
    forfait_epuise_body:               "Vos prochaines analyses seront facturées hors forfait, à l'unité.",
    forfait_depasse_body_singular:     "analyse supplémentaire ce mois-ci (facturation hors forfait à l'unité).",
    forfait_depasse_body_plural:       "analyses supplémentaires ce mois-ci (facturation hors forfait à l'unité).",
    // Pool d'une licence Entreprise consommé (28/09/2026) : rien n'est bloqué,
    // le dépassement est facturé à l'organisation. Même règle : aucun montant.
    pool_licence_epuise_title:         "Pool d'analyses de votre organisation épuisé",
    pool_licence_epuise_body:          "Vos analyses continuent : les prochaines sont facturées en dépassement à votre organisation.",
    quota_exhausted_dropzone:          'Dépôt indisponible — quota épuisé',
    quota_exhausted_project_doc:       'Ajout de document indisponible — votre quota d\'analyses est épuisé.',
    access_blocked_dropzone:           'Dépôt indisponible — analyses suspendues (voir le message en haut de page)',
    access_blocked_project_doc:        'Ajout de document indisponible — les analyses sont suspendues (voir le message en haut de page).',
    quota_remaining_label:             'analyses restantes',
    quota_contact_cta:                 'Nous contacter',

    // ── Type de mission ──
    mission_type_label:                'Type de mission',
    mission_type_required:             'Type de mission obligatoire',
    mission_type_avant_travaux:        'Avant travaux',
    mission_type_avant_demolition:     'Avant démolition',
    mission_type_avant_vente:          'Avant vente',
    mission_type_dta:                  'DTA complet (repérage, fiche récap, EP)',
    mission_type_dta_reperage:         'DTA — Repérage liste A et B',
    mission_type_fiche_recapitulative: 'Fiche récapitulative',
    mission_type_evaluation_periodique:'Évaluation périodique',
    mission_type_dapp:                 'DAPP',

    // ── Confirmation du type détecté ──
    type_conf_title:                   'Confirmation du type de rapport',
    type_conf_body:                    'Le type que vous avez déclaré ({declared}) ne correspond pas à ce que Normascan détecte dans le document ({detected}). Que souhaitez-vous faire ?',
    type_conf_source_regex:            'Détecté à partir du contenu textuel du PDF',
    type_conf_source_llm:              'Détecté après extraction structurée du rapport',
    type_conf_keep_declared:           'Conserver mon choix : {declared}',
    type_conf_use_detected:            'Utiliser le type détecté : {detected}',
    type_conf_cancel:                  'Annuler l’analyse',
    type_conf_timeout:                 'Le délai de confirmation (10 min) est dépassé. L’analyse a été annulée — vous pouvez en relancer une nouvelle.',

    // ── Type de rendu ──
    output_label:                      'Merci de sélectionner le type de rendu souhaité :',
    output_both:                       'Rapport d\'évaluation & Extraction',
    output_both_desc:                  'Vérification normative + extraction des données format Excel',
    output_html:                       'Rapport d\'évaluation',
    output_html_desc:                  'Vérification normative',
    output_excel:                      'Extraction Excel',
    output_excel_desc:                 'extraction des données format Excel',

    // ── Étapes du pipeline ──
    step_1:                            'Extraction',
    step_2:                            'Structuration',
    step_3:                            'Normalisation',
    step_4:                            'Classification',
    step_5:                            'Identification',
    step_6:                            'Cohérence',
    step_7:                            'Plans',
    step_8:                            'Validation',
    step_9:                            'Norma Vision',
    step_10:                           'Évaluation',
    step_11:                           'HTML',
    step_12:                           'Excel',
    step_starting:                     'Démarrage...',
    step_processing:                   'Analyse en cours...',
    step_in_progress:                  'En cours...',
    step_done:                         'Terminé',

    // ── Statuts d'analyse ──
    status_pending:                    'En attente',
    status_processing:                 'En cours',
    status_completed:                  'Terminé',
    status_error:                      'Erreur',
    status_cancelled:                  'Annulée',
    status_awaiting_type:              'En attente de confirmation',

    // ── Résultats ──
    samples_label:                     'prélèvement',
    samples_label_pl:                  'prélèvements',
    samples_positive:                  'positif',
    samples_positive_pl:               'positifs',
    samples_all_neg:                   'Tous négatifs',
    badge_excellent:                   'EXCELLENT',
    badge_good:                        'BON',
    badge_acceptable:                  'ACCEPTABLE',
    badge_insufficient:                'INSUFFISANT',
    level_excellent:                   'Excellent',
    level_bon:                         'Bon',
    level_acceptable:                  'Acceptable',
    level_insuffisant:                 'Insuffisant',
    analyses_label:                    'analyse',
    analyses_label_pl:                 'analyses',
    analyses_for:                      'pour',
    analyses_filters_title:            'Filtres',

    // ── Résumé copiable ──
    copy_summary:                      'Copier le résumé',
    copy_date:                         'Date',
    copy_score:                        'Indice',
    copy_samples:                      'Prélèvements',
    copy_positive:                     'positif',
    copy_positives:                    'positifs',
    copy_all_negative:                 'tous négatifs',
    copy_pdf_ready:                    'Rapport PDF prêt.',
    copy_excel_ready:                  'Excel prêt.',
    copy_both_ready:                   'PDF + Excel prêts.',
    copy_footer:                       'Rapport généré automatiquement par Normascan — Evalens\nBasé sur la norme NF X46-020',

    // ── Assistant NORMA ──
    norma_analysed:                    'analysé',
    norma_dont:                        'dont',
    norma_project_prefix:              'Projet :',
    norma_idle:       `Bonjour ! Je suis **NORMA**, votre assistant IA normatif.\n\nJe suis là pour vous aider à analyser vos documents de repérage amiante.\n\nDéposez un rapport au format PDF pour commencer.`,
    norma_uploaded:   `Document reçu. Vérification en cours...`,
    norma_validating: `Je vérifie la sécurité et la qualité de votre document...\n\nCette opération prend quelques secondes.`,
    norma_ready:      `Document validé ✓\n\nSélectionnez le type de rendu souhaité, puis démarrez l'analyse.`,
    norma_ready_scan: `Document validé ✓\n\nC'est un document **scanné** : au démarrage, je le lirai d'abord par reconnaissance de caractères (OCR) pour en extraire le texte. Cela prend un peu plus de temps.\n\nSélectionnez le type de rendu souhaité, puis démarrez l'analyse.`,
    norma_err_content:`Ce document ne semble pas être un rapport de repérage amiante.\n\nVeuillez déposer le bon fichier.`,
    norma_err_size:   `Ce document dépasse la limite de traitement autorisée.\n\nVeuillez réduire la taille du PDF ou le découper.`,
    norma_start:      `Démarrage de l'analyse...\n\nMaintenant je dois extraire et analyser selon la norme NF X 46-020 et le guide d'application FD X 46-041.`,
    norma_start_scan: `Lecture du document scanné...\n\nJe le passe d'abord à la reconnaissance de caractères (OCR) pour en extraire le texte, puis je vérifie qu'il s'agit bien d'un repérage amiante avant de l'analyser selon la norme NF X 46-020.`,
    norma_extract:    `Extraction en cours...\n\nLa structure est prête, je vais extraire les données clés par section.`,
    norma_data:       `Extraction des données métier...\n\nJe dois les identifier et les classer selon la Norme NF X 46-020.`,
    norma_validate:   `Validation approfondie...\n\nMaintenant je dois vérifier la cohérence des données extraites selon les exigences de la norme NF X46-020.`,
    norma_ml:         `Analyse visuelle...\n\n**NormaVision ML** je dois vérifier et analyser les éventuels schémas, cartographies, photos et annexes du rapport.`,
    norma_conformity: `Application des règles...\nJe termine mon appréciation et je rédige mon avis.`,
    // Les questions oui/non que NORMA pose elle-même (plus de fenêtre surgissante).
    norma_oui:          'Oui',
    norma_non:          'Non',
    norma_reponse_non:  'Très bien, je ne change rien.',
    norma_html:       `Génération du rapport...\n\nMise en forme des résultats.`,
    norma_final:      `Finalisation des exports...\n\nVos rendus seront disponibles dans quelques instants.`,

    // ── Vérifier une certification (chantier ⑩, 23/09/2026) ──
    certif_titre:                'Vérifier une certification',
    certif_intro:                'Saisissez le prénom et le nom d\'un diagnostiqueur pour voir ses certifications en cours, d\'après l\'annuaire officiel du ministère, mis à jour chaque jour.',
    certif_prenom:               'Prénom',
    certif_nom:                  'Nom',
    certif_rechercher:           'Rechercher',
    certif_recherche_en_cours:   'Recherche…',
    certif_source:               'Source : annuaire officiel des diagnostiqueurs immobiliers (data.gouv.fr).',
    certif_restantes:            '{n} recherche(s) restante(s) aujourd\'hui sur {limite}.',
    certif_limite_atteinte:      'Vous avez atteint la limite de 30 recherches pour aujourd\'hui. Revenez demain.',
    certif_saisie_incomplete:    'Indiquez le prénom et le nom du diagnostiqueur (2 caractères au moins chacun).',
    certif_source_indisponible:  'L\'annuaire officiel ne répond pas pour le moment. Réessayez dans quelques minutes : cette recherche n\'a pas été décomptée.',
    certif_trouvees:             'Certifications en cours de {nom}',
    certif_plusieurs:            'Plusieurs numéros de certificat répondent à ce nom : il peut s\'agir de personnes homonymes. Comparez le numéro avec celui indiqué dans le rapport.',
    certif_numero:               'N° de certificat',
    certif_organisme:            'Organisme certificateur',
    certif_col_domaine:          'Certificat',
    certif_col_debut:            'Du',
    certif_col_fin:              'Au',
    certif_mention_avec:         '✓ « Missions spécifiques » = certification AVEC MENTION : couvre notamment le repérage liste C.',
    certif_mention_sans:         'Sans mention : ne couvre pas les missions spécifiques (repérage liste C, immeubles de grande hauteur…).',
    certif_aucune:               'Aucune certification en cours trouvée pour {nom}.',
    certif_aucune_raisons:       'Cela ne signifie pas forcément que cette personne n\'est pas certifiée :',
    certif_raison_graphie:       'le nom est peut-être écrit autrement dans l\'annuaire (accents, prénom composé, nom d\'usage) ;',
    certif_raison_expiree:       'l\'annuaire ne conserve que les certifications en cours : une certification expirée n\'y figure plus ;',
    certif_raison_jamais:        'ou la personne n\'a pas de certification en cours à ce jour.',
    certif_avertissement:        'Normascan affiche ces informations telles que l\'annuaire officiel les publie, sans les modifier. En cas de doute, le certificat délivré par l\'organisme fait foi.',

    // ── Bandeau de suivi (chantier ⑮) : une analyse continue sur le serveur
    //    quand l'onglet est fermé ; à la réouverture, ce bandeau dit où elle en est.
    //    ⚠️ `{fichier}` et `{pct}` sont substitués par SuiviAnalysesBanner.js.
    suivi_en_cours:              'Votre analyse « {fichier} » est en cours ({pct} %). Elle continue même si vous fermez cette page : vous retrouverez ses conclusions dans Mes analyses.',
    suivi_en_file:               'Votre analyse « {fichier} » attend son tour. Elle démarrera seule : vous retrouverez ses conclusions dans Mes analyses.',
    suivi_terminee:              'Votre analyse « {fichier} » est terminée : retrouvez ses conclusions dans Mes analyses.',
    suivi_voir:                  'Voir mes analyses',
    suivi_fermer:                'Masquer ce message',

    // ── Analyse en cours : la voir, l'annuler (carte et page de l'analyse) ──
    analyse_btn_voir:            '👁️ Voir',
    analyse_btn_annuler:         '⏹️ Annuler l’analyse',
    // ⛔ Ne pas promettre « non décomptée » : elle l'est si l'IA avait déjà
    // travaillé dessus (`core/quotas.py::_SQL_ANALYSE_CONSOMMEE`).
    analyse_annulee:             '🚫 Cette analyse a été annulée.',
  },

  en: {
    // ── Choix de la norme au dépôt (T2) — affiché seulement s'il y en a plusieurs ──
    depot_norme_label:                 'Diagnostic type',
    depot_aucune_norme:                'No diagnostic type is enabled for your account. Contact Evalens to open one.',
    norme_non_activee:                 'This diagnostic type is not enabled for your account. Contact Evalens to open it.',
    norme_inconnue:                    'This diagnostic type does not exist or is no longer offered.',
    // ── Upload ──
    upload_placeholder:                'Drop or click + to upload your asbestos report',
    upload_dragging:                   'Drop your PDF report here...',
    upload_validating:                 'Validating...',
    val_pages:                         'pages',
    val_chars:                         'characters',
    val_limit_pct:                     '% of limit',
    plus_options_tooltip:              'More options',
    plus_associate_project:            'Associate a project',
    plus_no_projects:                  'No projects created',
    plus_draft:                        'Draft',

    // ── Quota exhausted ──
    quota_exhausted_badge:             'Quota reached',
    quota_exhausted_title:             'You have used all your analyses.',
    quota_exhausted_body:              'Choose your plan to continue.',
    quota_exhausted_cta:               'View plans',
    forfait_epuise_title:              'Monthly plan used up',
    forfait_epuise_body:               'Your next analyses will be billed individually, outside your plan.',
    forfait_depasse_body_singular:     'additional analysis this month (billed individually, outside your plan).',
    forfait_depasse_body_plural:       'additional analyses this month (billed individually, outside your plan).',
    pool_licence_epuise_title:         "Your organisation's analysis pool is used up",
    pool_licence_epuise_body:          'Your analyses continue: the next ones are billed to your organisation as overage.',
    quota_exhausted_dropzone:          'Upload unavailable — quota reached',
    quota_exhausted_project_doc:       'Document upload unavailable — your analysis quota is exhausted.',
    access_blocked_dropzone:           'Upload unavailable — analyses suspended (see the message at the top of the page)',
    access_blocked_project_doc:        'Document upload unavailable — analyses are suspended (see the message at the top of the page).',
    quota_remaining_label:             'analyses left',
    quota_contact_cta:                 'Contact us',

    // ── Mission type ──
    mission_type_label:                'Mission type',
    mission_type_required:             'Mission type is required',
    mission_type_avant_travaux:        'Before works',
    mission_type_avant_demolition:     'Before demolition',
    mission_type_avant_vente:          'Before sale',
    mission_type_dta:                  'Full DTA (survey + summary + EP)',
    mission_type_dta_reperage:         'DTA — Survey lists A & B',
    mission_type_fiche_recapitulative: 'Summary sheet',
    mission_type_evaluation_periodique:'Periodic assessment',
    mission_type_dapp:                 'DAPP',

    // ── Detected type confirmation ──
    type_conf_title:                   'Report type confirmation',
    type_conf_body:                    'The type you declared ({declared}) does not match what Normascan detected in the document ({detected}). What would you like to do?',
    type_conf_source_regex:            'Detected from the textual content of the PDF',
    type_conf_source_llm:              'Detected after structured report extraction',
    type_conf_keep_declared:           'Keep my choice: {declared}',
    type_conf_use_detected:            'Use detected type: {detected}',
    type_conf_cancel:                  'Cancel the analysis',
    type_conf_timeout:                 'Confirmation window (10 min) expired. The analysis has been cancelled.',

    // ── Output type ──
    output_label:                      'Please select the desired output type:',
    output_both:                       'Assessment Report & Extraction',
    output_both_desc:                  'Normative verification + Excel data extraction',
    output_html:                       'Assessment Report',
    output_html_desc:                  'Normative verification',
    output_excel:                      'Excel Extraction',
    output_excel_desc:                 'Excel data extraction',

    // ── Pipeline steps ──
    step_1:                            'PDF Extraction',
    step_2:                            'Structuring',
    step_3:                            'Admin data',
    step_4:                            'Samples',
    step_5:                            'Conclusions',
    step_6:                            'Norm gaps',
    step_7:                            'Plans',
    step_8:                            'Validation',
    step_9:                            'ML Analysis',
    step_10:                           'Assessment',
    step_11:                           'HTML',
    step_12:                           'Excel',
    step_starting:                     'Starting...',
    step_processing:                   'Analysis in progress...',
    step_in_progress:                  'In progress...',
    step_done:                         'Done',

    // ── Analysis statuses ──
    status_pending:                    'Pending',
    status_processing:                 'Processing',
    status_completed:                  'Completed',
    status_error:                      'Error',
    status_cancelled:                  'Cancelled',
    status_awaiting_type:              'Awaiting confirmation',

    // ── Results ──
    samples_label:                     'sample',
    samples_label_pl:                  'samples',
    samples_positive:                  'positive',
    samples_positive_pl:               'positives',
    samples_all_neg:                   'All negative',
    badge_excellent:                   'EXCELLENT',
    badge_good:                        'GOOD',
    badge_acceptable:                  'ACCEPTABLE',
    badge_insufficient:                'INSUFFICIENT',
    level_excellent:                   'Excellent',
    level_bon:                         'Good',
    level_acceptable:                  'Acceptable',
    level_insuffisant:                 'Insufficient',
    analyses_label:                    'analysis',
    analyses_label_pl:                 'analyses',
    analyses_for:                      'for',
    analyses_filters_title:            'Filters',

    // ── Copiable summary ──
    copy_summary:                      'Copy summary',
    copy_date:                         'Date',
    copy_score:                        'Index',
    copy_samples:                      'Samples',
    copy_positive:                     'positive',
    copy_positives:                    'positives',
    copy_all_negative:                 'all negative',
    copy_pdf_ready:                    'PDF report ready.',
    copy_excel_ready:                  'Excel ready.',
    copy_both_ready:                   'PDF + Excel ready.',
    copy_footer:                       'Report automatically generated by Normascan — Evalens\nBased on standard NF X46-020',

    // ── NORMA assistant ──
    norma_analysed:                    'analysed',
    norma_dont:                        'including',
    norma_project_prefix:              'Project:',
    norma_idle:       `Hello! I'm **NORMA**, your AI asbestos assistant.\n\nI'm here to help you analyse your asbestos survey reports before renovation or demolition works.\n\nDrop your PDF report to get started.`,
    norma_uploaded:   `Document received. Checking...`,
    norma_validating: `Checking the document...\n\nThis operation takes a few seconds.`,
    norma_ready:      `Document validated ✓\n\nSelect the desired output type, then start the analysis.`,
    norma_ready_scan: `Document validated ✓\n\nThis is a **scanned** document: when the analysis starts, I will first read it with optical character recognition (OCR) to extract its text. This takes a little longer.\n\nSelect the desired output type, then start the analysis.`,
    norma_err_content:`This document does not appear to be an asbestos survey report under standard **NF X46-020**.\n\nPlease upload the correct file.`,
    norma_err_size:   `This document exceeds the allowed processing limit.\n\nPlease reduce the PDF size or split it.`,
    norma_start:      `Starting analysis...\n\nInitialising the **NORMA** pipeline for your asbestos report.`,
    norma_start_scan: `Reading the scanned document...\n\nI first run optical character recognition (OCR) to extract its text, then check that it is indeed an asbestos survey before analysing it under NF X 46-020.`,
    norma_extract:    `Extraction in progress...\n\nStructure ready, extracting key data by section.`,
    norma_data:       `Extracting business data...\n\nRetrieving samples, surveys and ZPSO zones, conclusions and normative deviations.`,
    norma_validate:   `In-depth validation...\n\nChecking consistency of extracted data against NF X46-020 requirements.`,
    norma_ml:         `Visual plan analysis...\n\n**NormaVision ML** is examining implantation diagrams and report maps.`,
    norma_conformity: `Applying the rules...\nI'm finishing my review and drafting my opinion.`,
    norma_oui:          'Yes',
    norma_non:          'No',
    norma_reponse_non:  'All right, nothing changes.',
    norma_html:       `Generating report...\n\nFormatting analysis results.`,
    norma_final:      `Finalising exports...\n\nYour outputs will be available in a few moments.`,

    // ── Check a certification (project ⑩, 23/09/2026) ──
    certif_titre:                'Check a certification',
    certif_intro:                'Enter a diagnostician\'s first and last name to see their current certifications, from the official ministry directory, updated daily.',
    certif_prenom:               'First name',
    certif_nom:                  'Last name',
    certif_rechercher:           'Search',
    certif_recherche_en_cours:   'Searching…',
    certif_source:               'Source: official directory of property diagnosticians (data.gouv.fr).',
    certif_restantes:            '{n} search(es) left today out of {limite}.',
    certif_limite_atteinte:      'You have reached the limit of 30 searches for today. Please come back tomorrow.',
    certif_saisie_incomplete:    'Enter the diagnostician\'s first and last name (at least 2 characters each).',
    certif_source_indisponible:  'The official directory is not responding right now. Please try again in a few minutes: this search was not counted.',
    certif_trouvees:             'Current certifications of {nom}',
    certif_plusieurs:            'Several certificate numbers match this name: they may be different people with the same name. Compare the number with the one shown in the report.',
    certif_numero:               'Certificate no.',
    certif_organisme:            'Certification body',
    certif_col_domaine:          'Certificate',
    certif_col_debut:            'From',
    certif_col_fin:              'To',
    certif_mention_avec:         '✓ “Specific assignments” = certification WITH ENDORSEMENT: covers list C surveys in particular.',
    certif_mention_sans:         'Without endorsement: does not cover specific assignments (list C surveys, high-rise buildings…).',
    certif_aucune:               'No current certification found for {nom}.',
    certif_aucune_raisons:       'This does not necessarily mean this person is not certified:',
    certif_raison_graphie:       'the name may be spelled differently in the directory (accents, compound first name, married name);',
    certif_raison_expiree:       'the directory only keeps current certifications: an expired one no longer appears;',
    certif_raison_jamais:        'or the person has no current certification at this date.',
    certif_avertissement:        'Normascan shows this information exactly as the official directory publishes it. If in doubt, the certificate issued by the certification body prevails.',

    // ── Tracking banner (chantier ⑮) — `{fichier}` and `{pct}` are substituted.
    suivi_en_cours:              'Your analysis “{fichier}” is in progress ({pct}%). It keeps running even if you close this page: its conclusions will be in My analyses.',
    suivi_en_file:               'Your analysis “{fichier}” is waiting for its turn. It will start on its own: its conclusions will be in My analyses.',
    suivi_terminee:              'Your analysis “{fichier}” is complete: its conclusions are in My analyses.',
    suivi_voir:                  'View my analyses',
    suivi_fermer:                'Hide this message',

    analyse_btn_voir:            '👁️ View',
    analyse_btn_annuler:         '⏹️ Cancel the analysis',
    analyse_annulee:             '🚫 This analysis was cancelled.',
  },
};

export default analyse;
